// src/lib/partner/partnerAttention.js

import {
  fetchPartnerCohorts,
} from "./partnerCohorts";

import {
  fetchPartnerCohortProgress,
} from "./partnerProgress";

import {
  fetchPartnerLearnerFacilitators,
} from "./partnerFacilitators";


export const ATTENTION_STATUS = {
  INACTIVE: "inactive",
  WATCH: "watch",
  STALLED: "stalled",
};


const MANAGEMENT_ROLES = [
  "owner",
  "admin",
  "cohort_manager",
];


const WATCH_FROM_DAYS = 3;
const INACTIVE_FROM_DAYS = 7;
const STALLED_FROM_DAYS = 7;


/* -------------------------------------------------------------------------- */
/* Basic helpers                                                              */
/* -------------------------------------------------------------------------- */

function nullableNumber(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return null;
  }

  const parsed =
    Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : null;
}


function timestamp(value) {
  if (!value) {
    return 0;
  }

  const date =
    new Date(value);

  const time =
    date.getTime();

  return Number.isNaN(time)
    ? 0
    : time;
}


function daysSince(value) {
  const time =
    timestamp(value);

  if (!time) {
    return null;
  }

  const difference =
    Date.now() - time;

  return Math.max(
    0,
    Math.floor(
      difference /
        (1000 * 60 * 60 * 24)
    )
  );
}


function latestTimestamp(
  ...values
) {
  const valid =
    values
      .map((value) => ({
        value,
        time: timestamp(value),
      }))
      .filter(
        (item) =>
          item.time > 0
      )
      .sort(
        (a, b) =>
          b.time - a.time
      );

  return valid[0]?.value ||
    null;
}


function uniqueProgressRows(
  rows
) {
  const map =
    new Map();

  for (const row of rows) {
    const key =
      row?.cohort_learner_id ||
      `${row?.cohort_id || ""}:${row?.user_id || ""}`;

    if (!key) {
      continue;
    }

    map.set(
      String(key),
      row
    );
  }

  return Array.from(
    map.values()
  );
}


/* -------------------------------------------------------------------------- */
/* Attention calculation                                                      */
/* -------------------------------------------------------------------------- */

export function getPartnerAttentionStatus(
  learner
) {
  if (
    learner?.membership_status !==
    "active"
  ) {
    return null;
  }


  /*
   * "Meaningful activity" comes from the existing
   * partner cohort progress RPC.
   *
   * If the learner has never recorded activity,
   * use joined_at as the age of the inactive state.
   */
  const activityDays =
    nullableNumber(
      learner?.days_since_activity
    ) ??
    daysSince(
      learner?.last_activity_at
    ) ??
    daysSince(
      learner?.joined_at
    );


  /*
   * A learner has started their journey when we can
   * see completed learning or a current learning
   * position.
   */
  const startedJourney =
    (
      nullableNumber(
        learner?.levels_completed
      ) || 0
    ) > 0 ||
    Boolean(
      learner?.current_quest_id ||
        learner?.current_subquest_id ||
        learner?.current_level_key
    );


  /*
   * Progression is not the same thing as simply
   * opening/logging into the application.
   *
   * We use the newest learning/task progression
   * timestamp available from the existing RPC.
   */
  const latestProgressAt =
    latestTimestamp(
      learner
        ?.current_position_updated_at,
      learner
        ?.last_completed_at,
      learner
        ?.last_task_completed_at
    );


  const progressDays =
    daysSince(
      latestProgressAt ||
        (
          startedJourney
            ? learner?.joined_at
            : null
        )
    );


  /*
   * STALLED comes first.
   *
   * A stalled learner may also technically have
   * been inactive for 7 days, but we deliberately
   * classify them as Stalled so every learner only
   * appears in one attention category.
   */
  if (
    startedJourney &&
    progressDays !== null &&
    progressDays >=
      STALLED_FROM_DAYS
  ) {
    return {
      key:
        ATTENTION_STATUS.STALLED,

      label:
        "Stalled",

      days:
        progressDays,

      detail:
        `No learning progression for ${progressDays} ${
          progressDays === 1
            ? "day"
            : "days"
        }`,
    };
  }


  if (
    activityDays !== null &&
    activityDays >=
      INACTIVE_FROM_DAYS
  ) {
    return {
      key:
        ATTENTION_STATUS.INACTIVE,

      label:
        "Inactive",

      days:
        activityDays,

      detail:
        learner?.last_activity_at
          ? `Last meaningful activity ${activityDays} ${
              activityDays === 1
                ? "day"
                : "days"
            } ago`
          : `No meaningful activity for ${activityDays} ${
              activityDays === 1
                ? "day"
                : "days"
            }`,
    };
  }


  if (
    activityDays !== null &&
    activityDays >=
      WATCH_FROM_DAYS &&
    activityDays <
      INACTIVE_FROM_DAYS
  ) {
    return {
      key:
        ATTENTION_STATUS.WATCH,

      label:
        "Watch",

      days:
        activityDays,

      detail:
        learner?.last_activity_at
          ? `Last meaningful activity ${activityDays} ${
              activityDays === 1
                ? "day"
                : "days"
            } ago`
          : `No meaningful activity for ${activityDays} ${
              activityDays === 1
                ? "day"
                : "days"
            }`,
    };
  }


  return null;
}


/* -------------------------------------------------------------------------- */
/* Main dataset                                                               */
/* -------------------------------------------------------------------------- */

export async function fetchPartnerAttentionDataset(
  partnerRole
) {
  const cohortsResponse =
    await fetchPartnerCohorts();

  const cohorts =
    Array.isArray(
      cohortsResponse
    )
      ? cohortsResponse
      : [];


  const activeCohorts =
    cohorts.filter(
      (cohort) =>
        cohort.status ===
        "active"
    );


  if (
    activeCohorts.length ===
    0
  ) {
    return {
      cohorts,
      activeCohorts,
      progressRows: [],
      activeParticipants: [],
      attentionRows: [],
    };
  }


  /*
   * Each cohort-progress call remains protected by
   * the existing Partner backend permissions.
   *
   * Facilitators therefore receive only the
   * learners they are allowed to see.
   */
  const progressChunks =
    await Promise.all(
      activeCohorts.map(
        async (cohort) => {
          const rows =
            await fetchPartnerCohortProgress(
              cohort.id
            );

          return Array.isArray(
            rows
          )
            ? rows
            : [];
        }
      )
    );


  const progressRows =
    uniqueProgressRows(
      progressChunks.flat()
    );


  const canViewFacilitators =
    MANAGEMENT_ROLES.includes(
      partnerRole
    );


  let facilitatorRows = [];


  /*
   * Do NOT call the management facilitator directory
   * for Facilitators/Viewers.
   */
  if (canViewFacilitators) {
    const facilitatorChunks =
      await Promise.all(
        activeCohorts.map(
          async (cohort) => {
            const rows =
              await fetchPartnerLearnerFacilitators(
                cohort.id
              );

            return Array.isArray(
              rows
            )
              ? rows
              : [];
          }
        )
      );

    facilitatorRows =
      facilitatorChunks.flat();
  }


  const cohortMap =
    new Map(
      activeCohorts.map(
        (cohort) => [
          String(cohort.id),
          cohort,
        ]
      )
    );


  const facilitatorMap =
    new Map(
      facilitatorRows.map(
        (assignment) => [
          String(
            assignment
              .cohort_learner_id
          ),
          assignment,
        ]
      )
    );


  const activeParticipants =
    progressRows.filter(
      (learner) =>
        learner
          .membership_status ===
        "active"
    );


  const attentionRows =
    activeParticipants
      .map((learner) => {
        const attention =
          getPartnerAttentionStatus(
            learner
          );

        if (!attention) {
          return null;
        }


        const assignment =
          facilitatorMap.get(
            String(
              learner
                .cohort_learner_id
            )
          ) || null;


        let facilitatorName =
          "—";


        if (
          canViewFacilitators
        ) {
          facilitatorName =
            assignment
              ?.facilitator_partner_member_id
              ? assignment
                  .facilitator_full_name ||
                assignment
                  .facilitator_email ||
                "Facilitator"
              : "Unassigned";
        } else if (
          partnerRole ===
          "facilitator"
        ) {
          /*
           * A Facilitator only receives their
           * authorised learners from the progress
           * RPC. Do not expose the wider facilitator
           * assignment directory.
           */
          facilitatorName =
            "You";
        }


        return {
          ...learner,

          cohort_name:
            learner.cohort_name ||
            cohortMap.get(
              String(
                learner.cohort_id
              )
            )?.name ||
            "Cohort",

          attention_status:
            attention.key,

          attention_label:
            attention.label,

          attention_detail:
            attention.detail,

          attention_days:
            attention.days,

          facilitator_name:
            facilitatorName,

          facilitator_assignment:
            assignment,
        };
      })
      .filter(Boolean)
      .sort(
        (a, b) => {
          const priority = {
            stalled: 1,
            inactive: 2,
            watch: 3,
          };

          return (
            (
              priority[
                a.attention_status
              ] || 99
            ) -
              (
                priority[
                  b.attention_status
                ] || 99
              ) ||
            (
              b.attention_days || 0
            ) -
              (
                a.attention_days || 0
              )
          );
        }
      );


  return {
    cohorts,
    activeCohorts,
    progressRows,
    activeParticipants,
    attentionRows,
  };
}


/* -------------------------------------------------------------------------- */
/* Counts                                                                     */
/* -------------------------------------------------------------------------- */

export function getPartnerAttentionCounts(
  attentionRows
) {
  const rows =
    Array.isArray(
      attentionRows
    )
      ? attentionRows
      : [];


  return {
    all:
      rows.length,

    inactive:
      rows.filter(
        (row) =>
          row.attention_status ===
          ATTENTION_STATUS.INACTIVE
      ).length,

    watch:
      rows.filter(
        (row) =>
          row.attention_status ===
          ATTENTION_STATUS.WATCH
      ).length,

    stalled:
      rows.filter(
        (row) =>
          row.attention_status ===
          ATTENTION_STATUS.STALLED
      ).length,
  };
}